// https://material-ui.com/api/text-field/
// https://www.npmjs.com/package/react-number-format

import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { compose } from 'redux';
import { withStyles } from '@material-ui/styles';
import withGuid from "../../hocs/with_guid.js";
import withHelperText from "../../hocs/with_helper_text.js";

import { TextField as MaterialTextField, FormLabel } from '@material-ui/core';

import { 
  InputVariantStandard, InputVariantOutlined, InputVariantFilled,
  InputLabelDisplayModeInside, InputLabelDisplayModeAbove,
  TextFieldTypeText, TextFieldTypeNumber, TextFieldTypeEmail,
  TextFieldCapitalizationNone, TextFieldCapitalizationCapitalize,
  TextFieldCapitalizationUppercase, TextFieldCapitalizationLowercase
} from "./constants.js";

const styles = theme => ({
});

/**
 * Callback for inputs
 * @callback TextField~onChange
 * @param {string} value input's new value
 */

/**
 * Component for input simple text, number, email. Already includes HOCs {@link WithGuid}, {@link WithHelperText}
 * @name TextField
 * @param {object} props React component properties
 * @param {any} [props.value] Field's value
 * @param {string} [props.displayName] Field's display name
 * @param {boolean} [props.hasErrors = false] Does errors exists on the field?
 * @param {Array.<string>} [props.errors = []] Error messages array
 * @param {boolean} [props.readOnly = false] Does field is read only?
 * @param {string} [props.type = "text"] Type of data in text field. Available values: 
 *  - "text" 
 *  - "number" 
 *  - "email"
 * 
 * NOTE: for providing type, please, import necessary type as constant from file "components/base/inputs/constants.js.jsx"
 * @param {string} [props.className] CSS-class of button
 * @param {string} [props.variant = "outlined"] View variant for text field stylization. Available values:
 *  - "standard"
 *  - "outlined"
 *  - "filled"
 * 
 * Explanation of variants: {@link https://material-ui.com/components/text-fields/#textfield}
 * 
 * NOTE: for providing variant, please, import necessary variant as constant from file "components/base/inputs/constants.js.jsx"
 * @param {string} [props.displayNamePosition = "inside"] Position of field's display name: inside the field with floating style or above. Available values:
 *  - "inside"
 *  - "above"
 * 
 * NOTE: for providing position, please, import necessary position as constant from file "components/base/inputs/constants.js.jsx"
 * @param {boolean} [props.multiline = false] Does field multiline? (Looks like textarea)
 * @param {number} [props.rows = 5] Number of lines (works only if props.multiline == true)
 * @param {string} [props.capitalization = "none"] Capitalization style for field. Available values:
 *  - "none"
 *  - "capitalize"
 *  - "uppercase"
 *  - "lowercase"
 * 
 * NOTE: for providing capitalization mode, please, import necessary mode as constant from file "components/base/inputs/constants.js.jsx"
 * @param {boolean} [props.propsPrior = false] If it == true, value from prop will be priority for state value
 * @param {TextField~onChange} [props.onChange] On change handler
 * @param {func} [props.onBlur] On blur (lose focus) event handler
 * @example
 * return (
 *   <TextField className={ "awesome-class" } 
 *              onChange = { (value) => {
 *                // Some actions
 *              } } 
 *              value = { "value" } />
 * )
*/

class TextField extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      
    }
  }

  static getDerivedStateFromProps(props, state) {
    let value;

    let propsValue   = `${props.value || ""}`;
    let defaultValue = `${state.defaultValue || ""}`;
    let stateValue   = `${state.value || ""}`;
    
    if(defaultValue !== propsValue) {
      defaultValue = propsValue;
      value = propsValue;
    }
    else {
      if(props.propsPrior) {
        defaultValue = propsValue;
        value = propsValue;
      }
      else {
        value = stateValue;
      }
      
    }

    return {
      value: value,
      defaultValue: defaultValue
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return this.state.value !== nextState.value ||
           this.props.displayName !== nextProps.displayName ||
           this.props.hasError !== nextProps.hasError ||
           this.props.readOnly !== nextProps.readOnly;
  }

  onChange = (event) => {
    const { onChange, capitalization } = this.props;
    let value = (event.target.value || "").trimLeft();
    if(capitalization === TextFieldCapitalizationUppercase) {
      value = value.toUpperCase();
    }

    this.setState({
      value: value
    });

    if(onChange) {
      onChange(value);
    }
  }

  onBlur = () => {
    const { onBlur } = this.props;
    if(onBlur) {
      onBlur();
    }
  }

  type() {
    return this.props.type;
  }

  inputProps() {
    const { variant, underline } = this.props;
    let inputProps = {};
    if(variant === InputVariantFilled) {
      inputProps.disableUnderline = !underline;
    }
    return inputProps;
  }

  render() {
    const { classes, className, guid, hasError, placeholder } = this.props;

    const { name, displayName, variant, displayNamePosition, readOnly, multiline, rows, disableAutoComplete } = this.props;

    const { value } = this.state;

    return <React.Fragment>
      {
        displayNamePosition === InputLabelDisplayModeAbove &&
        displayName && 
        displayName !== "" &&
        <FormLabel htmlFor = { guid }
                   error = { hasError } >
          { `${displayName}:` }
        </FormLabel>
      }
      <MaterialTextField className = { clsx(classes.root, className) } 
                         id = { guid } 
                         inputRef = { (ref) => this.textField = ref }
                         fullWidth
                         error = { hasError } 
                         InputProps = { this.inputProps() }
                         type = { this.type() }
                         name = { disableAutoComplete ? guid : name }
                         label = { displayNamePosition === InputLabelDisplayModeInside ? displayName : undefined }
                         variant = { variant }
                         value = { value ? value : "" } 
                         onChange = { this.onChange } 
                         onBlur = { this.onBlur }
                         disabled = { readOnly }
                         multiline = { multiline }
                         rows = { rows } 
                         placeholder = { placeholder ? placeholder : "" }/>
    </React.Fragment>
  }
}

TextField.propTypes = {
  variant: PropTypes.oneOf([InputVariantStandard, InputVariantOutlined, InputVariantFilled]),
  type: PropTypes.oneOf([TextFieldTypeText, TextFieldTypeEmail, TextFieldTypeNumber]),
  displayNamePosition: PropTypes.oneOf([InputLabelDisplayModeAbove, InputLabelDisplayModeInside]),
  propsPrior: PropTypes.bool,
  capitalization: PropTypes.string,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  readOnly: PropTypes.bool,
};

TextField.defaultProps = {
  variant: InputVariantOutlined,
  type: TextFieldTypeText,
  displayNamePosition: InputLabelDisplayModeInside,
  underline: true,
  propsPrior: false,
  capitalization: TextFieldCapitalizationNone,
  multiline: false,
  rows: 5,
  readOnly: false
}

export default compose(withGuid, withHelperText, withStyles(styles))(TextField)
export { TextField as RawTextField };
